import { useSeoMeta } from "@unhead/react";
import {
  ArrowRight,
  Bitcoin,
  Check,
  Gauge,
  MessageSquare,
  PackageCheck,
  PencilRuler,
  RefreshCw,
  Rocket,
  ShieldCheck,
  Sparkles,
  Zap,
} from "lucide-react";

import { SiteHeader } from "@/components/cashcraft/SiteHeader";
import { SiteFooter } from "@/components/cashcraft/SiteFooter";
import { OrderForm } from "@/components/cashcraft/OrderForm";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import {
  EXTRA_REVISION_FEE_USD,
  PRICING_TIERS,
  REVISION_WINDOW_DAYS,
  TIER_REVISIONS,
  TIER_USD,
} from "@/lib/cashcraft";
import { useBchPrice, usdToBch } from "@/hooks/useBchPrice";

const STEPS = [
  {
    icon: MessageSquare,
    title: "Describe your site",
    body: "Tell us what you want in plain words — the type of site, your style, and any features. Takes two minutes.",
  },
  {
    icon: PencilRuler,
    title: "We craft it",
    body: "We design and build a fast, modern site tailored to your business — with Bitcoin Cash baked in.",
  },
  {
    icon: Rocket,
    title: "Launch it",
    body: "We deploy it live and hand you the keys — ready for business from day one.",
  },
];

const PERKS = [
  { icon: Bitcoin, title: "Bitcoin Cash ready", body: "Want your new site to accept Bitcoin Cash? We build it in — but it's always optional." },
  { icon: Gauge, title: "Fast & modern", body: "Clean, responsive designs that load instantly and look great on any device." },
  { icon: Zap, title: "Quick turnaround", body: "Most sites delivered in days, not months. Describe it today, launch it this week." },
  { icon: ShieldCheck, title: "You own it", body: "Your site, your keys, your money. No lock-in, no surprise fees." },
];

const FAQS = [
  { q: "How does payment in Bitcoin Cash work?", a: "Once we scope your project we send a quote. You pay in BCH — peer to peer, no card processors, no chargebacks. We can also invoice a deposit up front and the balance on delivery." },
  { q: "What do I need to get started?", a: "Just a description of the site you want. Fill out the project form and hit send — no account or sign-up required. Your request is encrypted automatically." },
  { q: "How long does it take?", a: "A simple one-page site can be ready in about 48 hours. Multi-page sites and stores usually take a few days depending on scope." },
  { q: "Is my information private?", a: "Yes. Your project details are end-to-end encrypted when you submit them — only BCH Launch can read them. We never publish your contact info." },
  { q: "Can my site accept Bitcoin Cash payments?", a: "Absolutely — that's our specialty. We wire up BCH payments so you can accept peer-to-peer digital cash directly, no custodian required." },
  { q: "How do revisions work?", a: "Each plan includes a set number of revision rounds: Starter 1, Growth 2, Commerce 3. A round is one consolidated batch of changes sent together — so gather all your tweaks into a single list rather than sending them one at a time. Revisions apply within 7 days of delivery. Extra rounds are a flat $40 each (in BCH). Brand-new pages, features, or a change in direction after approval are a separate quote." },
  { q: "Do I own the finished site?", a: "100%. When we deliver, the site and everything in it is yours. No lock-in and no ongoing fees unless you want us to host or maintain it." },
];

const Index = () => {
  const { data: bchUsd } = useBchPrice();

  useSeoMeta({
    title: "BCH Launch — Describe it. Build it. Launch it.",
    description:
      "Custom websites, delivered fast and paid in Bitcoin Cash. Describe the site you want and we'll build and launch it for you — BCH payments built in.",
    ogTitle: "BCH Launch — Describe it. Build it. Launch it.",
    ogDescription:
      "Custom websites, delivered fast — paid in Bitcoin Cash. Describe it, we build it, you launch it.",
    ogImage: "https://bch-launch.netlify.app/og-image.jpg",
    ogUrl: "https://bch-launch.netlify.app/",
    ogType: "website",
    twitterCard: "summary_large_image",
    twitterImage: "https://bch-launch.netlify.app/og-image.jpg",
  });

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteHeader />

      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute left-1/2 top-[-10%] h-[500px] w-[500px] -translate-x-1/2 rounded-full bg-primary/20 blur-[120px]" />
            <div className="absolute bottom-[-20%] right-[-10%] h-[400px] w-[400px] rounded-full bg-primary/10 blur-[120px]" />
          </div>

          <div className="container mx-auto px-4 py-20 text-center sm:px-6 sm:py-28">
            <div className="mx-auto mb-6 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
              <Sparkles className="h-4 w-4" />
              Peer-to-peer web design
            </div>
            <h1 className="mx-auto max-w-4xl text-4xl font-extrabold leading-tight tracking-tight sm:text-6xl">
              Describe it.{" "}
              <span className="bg-gradient-to-r from-primary to-emerald-400 bg-clip-text text-transparent">
                Build it. Launch it.
              </span>
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground sm:text-xl">
              Custom websites, delivered fast. Tell us what you want in plain
              words and we'll launch it for you. Pay however you like — by card
              in US dollars, or in Bitcoin Cash. Dollars are totally fine!
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button asChild size="lg" className="rounded-full px-8 text-base font-semibold">
                <a href="#start">
                  Start your project <ArrowRight className="ml-1 h-4 w-4" />
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full px-8 text-base">
                <a href="#pricing">See pricing</a>
              </Button>
            </div>
            <p className="mt-6 text-sm text-muted-foreground">
              Pay by card in USD · or in Bitcoin Cash · whatever's easiest for you
            </p>
          </div>
        </section>

        {/* Perks */}
        <section className="border-y border-border/60 bg-muted/20">
          <div className="container mx-auto grid gap-6 px-4 py-14 sm:grid-cols-2 sm:px-6 lg:grid-cols-4">
            {PERKS.map((perk) => (
              <div key={perk.title} className="flex flex-col gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15 text-primary">
                  <perk.icon className="h-5 w-5" />
                </div>
                <h3 className="font-semibold">{perk.title}</h3>
                <p className="text-sm text-muted-foreground">{perk.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section id="how-it-works" className="scroll-mt-20">
          <div className="container mx-auto px-4 py-20 sm:px-6">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                How it works
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Three simple steps from idea to a live website.
              </p>
            </div>
            <div className="mt-14 grid gap-6 md:grid-cols-3">
              {STEPS.map((step, i) => (
                <Card key={step.title} className="relative border-border/60 bg-card">
                  <CardContent className="p-8">
                    <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/15 text-primary">
                      <step.icon className="h-6 w-6" />
                    </div>
                    <div className="mb-2 text-sm font-semibold text-primary">
                      Step {i + 1}
                    </div>
                    <h3 className="text-xl font-bold">{step.title}</h3>
                    <p className="mt-2 text-muted-foreground">{step.body}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section id="pricing" className="scroll-mt-20 border-y border-border/60 bg-muted/20">
          <div className="container mx-auto px-4 py-20 sm:px-6">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                Simple, transparent pricing
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Priced in US dollars — pay by card or in Bitcoin Cash, your
                choice. Need something bigger? Request a custom quote.
              </p>
            </div>
            <div className="mx-auto mt-14 grid max-w-5xl gap-6 lg:grid-cols-3">
              {PRICING_TIERS.map((tier) => (
                <Card
                  key={tier.id}
                  className={cn(
                    "relative flex flex-col border-border/60 bg-card",
                    tier.highlighted &&
                      "border-primary/60 shadow-xl shadow-primary/10 ring-1 ring-primary/30 lg:-translate-y-2",
                  )}
                >
                  {tier.highlighted && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
                      Most popular
                    </div>
                  )}
                  <CardContent className="flex flex-1 flex-col p-8">
                    <h3 className="text-lg font-bold">{tier.name}</h3>
                    <div className="mt-2 flex items-baseline gap-1">
                      <span className="text-4xl font-extrabold">{tier.priceUsd}</span>
                      <span className="text-sm text-muted-foreground">USD</span>
                    </div>
                    <div className="mt-1 flex items-center gap-1.5 text-sm font-medium text-primary">
                      <Bitcoin className="h-3.5 w-3.5" />
                      {bchUsd ? (
                        <span>
                          ≈ {usdToBch(TIER_USD[tier.id], bchUsd)} BCH
                        </span>
                      ) : (
                        <span className="text-muted-foreground">
                          loading BCH price…
                        </span>
                      )}
                    </div>
                    <p className="mt-3 text-sm text-muted-foreground">
                      {tier.tagline}
                    </p>
                    <ul className="mt-6 flex-1 space-y-3">
                      {tier.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm">
                          <span className="mt-0.5 text-primary">✓</span>
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      asChild
                      className={cn(
                        "mt-8 w-full rounded-full font-semibold",
                        !tier.highlighted && "bg-secondary text-secondary-foreground hover:bg-secondary/80",
                      )}
                    >
                      <a href="#start">Choose {tier.name}</a>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="mt-8 text-center text-sm text-muted-foreground">
              Pick a plan above to start your project — you'll pay a 50% deposit
              once your order's in, by card (USD) or Bitcoin Cash.
            </p>
            <p className="mt-2 text-center text-sm text-muted-foreground">
              Want us to host and maintain it too?{" "}
              <a
                href="/hosting"
                className="font-medium text-primary hover:underline"
              >
                See hosting plans →
              </a>
            </p>
          </div>
        </section>

        {/* Revision policy */}
        <section id="revisions" className="scroll-mt-20">
          <div className="container mx-auto px-4 py-20 sm:px-6">
            <div className="mx-auto max-w-3xl">
              <div className="mb-10 text-center">
                <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                  Fair revision policy
                </h2>
                <p className="mt-4 text-lg text-muted-foreground">
                  Every plan includes revision rounds so we get it just right —
                  with clear limits that keep things fast and fair for everyone.
                </p>
              </div>

              <div className="mb-8 grid gap-4 sm:grid-cols-3">
                {PRICING_TIERS.map((tier) => (
                  <Card key={tier.id} className="border-border/60 bg-card text-center">
                    <CardContent className="p-6">
                      <RefreshCw className="mx-auto mb-3 h-6 w-6 text-primary" />
                      <div className="text-3xl font-extrabold">
                        {TIER_REVISIONS[tier.id]}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        revision {TIER_REVISIONS[tier.id] === 1 ? "round" : "rounds"} · {tier.name}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card className="border-primary/30 bg-primary/5">
                <CardContent className="p-6 sm:p-8">
                  <div className="flex items-start gap-3">
                    <PackageCheck className="mt-0.5 h-6 w-6 shrink-0 text-primary" />
                    <div>
                      <h3 className="font-bold">
                        One round = one batch of changes
                      </h3>
                      <p className="mt-1 text-muted-foreground">
                        Send all your tweaks together in a single list, and
                        we'll knock them out in one go. That's a round. Sending
                        changes one-at-a-time uses them up fast — so gather them
                        up first!
                      </p>
                    </div>
                  </div>
                  <ul className="mt-6 grid gap-3 text-sm sm:grid-cols-2">
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>
                        Revisions apply within{" "}
                        <strong>{REVISION_WINDOW_DAYS} days</strong> of delivery.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>
                        Extra rounds:{" "}
                        <strong>${EXTRA_REVISION_FEE_USD} each</strong>, paid in
                        BCH.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>
                        In scope: colors, copy, layout tweaks, image swaps.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>
                        New pages, features, or a new direction = a fresh quote.
                      </span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Order form */}
        <section id="start" className="scroll-mt-20 border-y border-border/60 bg-muted/20">
          <div className="container mx-auto px-4 py-20 sm:px-6">
            <div className="mx-auto max-w-3xl">
              <div className="mb-10 text-center">
                <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                  Start your project
                </h2>
                <p className="mt-4 text-lg text-muted-foreground">
                  Tell us what you want. We'll craft it and get back to you with
                  a quote.
                </p>
              </div>
              <OrderForm />
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="scroll-mt-20">
          <div className="container mx-auto px-4 py-20 sm:px-6">
            <div className="mx-auto max-w-3xl">
              <div className="mb-10 text-center">
                <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                  Common questions
                </h2>
              </div>
              <Accordion type="single" collapsible className="w-full">
                {FAQS.map((faq, i) => (
                  <AccordionItem key={i} value={`item-${i}`}>
                    <AccordionTrigger className="text-left text-base font-semibold">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};

export default Index;
